import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { AssigneeCell } from '@/components/compliance/AssigneeCell';
import { EvidencePanel } from '@/components/compliance/EvidencePanel';
import { ScopeInheritanceChain } from '@/components/compliance/ScopeInheritanceChain';
import { AssignmentPanel } from '@/features/compliance/assignments/AssignmentPanel';
import { useEffectiveAssignment } from '@/features/compliance/assignments/scope-api';
import { useControl, useFrameworks } from '@/api/hooks/useCompliance';
import type { ScopeAssignment, Assignment } from '@grc/shared';

const TIER_BADGE: Record<string, { label: string; variant: 'emerald' | 'sky' | 'violet' | 'muted' }> = {
  control:   { label: 'L3',                      variant: 'emerald' },
  category:  { label: 'L2 (inherited from category)', variant: 'sky' },
  framework: { label: 'L1 (inherited from framework)', variant: 'violet' },
  none:      { label: 'Unassigned',              variant: 'muted' },
};

function isScopeAssignment(a: ScopeAssignment | Assignment | null): a is ScopeAssignment {
  return a !== null && 'framework_id' in a;
}

export function ControlDetailPage() {
  const { frameworkId, controlId } = useParams<{ frameworkId: string; controlId: string }>();

  const { data: control, isLoading: controlLoading } = useControl(controlId ?? '');
  const { data: frameworks = [] } = useFrameworks();
  const { data: effective, isLoading: effectiveLoading } = useEffectiveAssignment(controlId ?? '');

  const framework = frameworks.find((f) => f.id === frameworkId);

  const tier = effective?.tier ?? 'none';
  const tierBadge = TIER_BADGE[tier] ?? TIER_BADGE.none;

  let effectiveAssignee: { displayName?: string | null; email?: string | null; isPending?: boolean } = {};
  if (effective?.assignment) {
    const a = effective.assignment;
    effectiveAssignee = {
      displayName: (a as ScopeAssignment).assignee_display_name ?? (a as Assignment).assignee_display_name ?? null,
      email: (a as ScopeAssignment).assignee_email ?? (a as Assignment).assignee_email ?? null,
      isPending: (a as ScopeAssignment).assignee_is_pending ?? (a as Assignment).assignee_is_pending ?? false,
    };
  }

  const scopeAssignmentForPanel = effective?.assignment && isScopeAssignment(effective.assignment)
    ? effective.assignment
    : null;

  useEffect(() => {
    if (control && framework) {
      document.title = `${control.control_id} — ${framework.display_name} — VeriGRC`;
    }
  }, [control, framework]);

  if (controlLoading || effectiveLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-6 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!control) {
    return (
      <div className="p-6 text-sm text-text-secondary">Control not found.</div>
    );
  }

  return (
    <div className="space-y-8 max-w-4xl">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-xs text-text-secondary flex-wrap">
        <Link to="/compliance/workspace/frameworks" className="hover:text-text-primary transition-colors">Compliance</Link>
        <ChevronRight className="h-3 w-3 shrink-0" />
        <Link to={`/compliance/controls/${frameworkId}`} className="hover:text-text-primary transition-colors">
          {framework ? `${framework.display_name} v${framework.version}` : 'Framework'}
        </Link>
        {control.category && (
          <>
            <ChevronRight className="h-3 w-3 shrink-0" />
            <Link to={`/compliance/controls/${frameworkId}`} className="hover:text-text-primary transition-colors">
              {control.category}
            </Link>
          </>
        )}
        <ChevronRight className="h-3 w-3 shrink-0" />
        <span className="text-text-primary font-medium">{control.control_id} — {control.title}</span>
      </nav>

      {/* Assignee identity header */}
      <div className="flex items-center gap-3 rounded-lg border border-border-subtle bg-bg-surface p-4">
        <div className="flex-1">
          <p className="text-[10px] text-text-secondary uppercase tracking-wider mb-1">Effective assignee</p>
          <AssigneeCell
            displayName={effectiveAssignee.displayName}
            email={effectiveAssignee.email}
            isPending={effectiveAssignee.isPending}
          />
        </div>
        <Badge variant={tierBadge.variant} className="text-[9px] shrink-0">{tierBadge.label}</Badge>
      </div>

      {/* Assignment section */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-text-primary">Assignments</h2>
        <AssignmentPanel
          controlId={control.id}
          frameworkId={frameworkId}
          effectiveTier={tier}
          scopeAssignment={scopeAssignmentForPanel}
          layout="page"
        />
      </section>

      {/* Evidence section */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-text-primary">Evidence</h2>
        <EvidencePanel controlId={control.id} layout="page" />
      </section>

      {/* Scope inheritance chain */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-text-primary">Scope inheritance</h2>
        <ScopeInheritanceChain
          frameworkId={control.framework_id}
          controlId={control.id}
          controlCategory={control.category ?? null}
          effectiveTier={tier}
        />
      </section>

      {/* Framework context card */}
      {(control.subcategory || control.function) && (
        <section className="rounded-lg border border-border-subtle bg-bg-surface p-4 space-y-1.5">
          <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider">Control context</p>
          {control.category && (
            <div className="flex gap-2">
              <span className="text-xs text-text-secondary w-24 shrink-0">Category</span>
              <span className="text-xs text-text-primary">{control.category}</span>
            </div>
          )}
          {control.subcategory && (
            <div className="flex gap-2">
              <span className="text-xs text-text-secondary w-24 shrink-0">Subcategory</span>
              <span className="text-xs text-text-primary">{control.subcategory}</span>
            </div>
          )}
          {control.function && (
            <div className="flex gap-2">
              <span className="text-xs text-text-secondary w-24 shrink-0">Function</span>
              <span className="text-xs text-text-primary">{control.function}</span>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
